"use client";

import { LaunchOfferBar } from "@/components/marketing/LaunchOfferBar";
import { OfferPopup } from "@/components/marketing/OfferPopup";
import { api } from "@/lib/api";
import { fallbackPrice, planPrices } from "@/lib/planPricing";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  ArrowRight,
  BookOpen,
  Building2,
  Check,
  ChevronDown,
  ChevronRight,
  FileSearch,
  Gauge,
  Home,
  HelpCircle,
  LifeBuoy,
  History,
  LogOut,
  Mail,
  Database,
  Menu,
  Shield,
  Sparkles,
  Tag,
  Telescope,
  UserRound,
  Users,
  Workflow,
  X,
  type LucideIcon,
  Clapperboard,
  Gift,
  Handshake,
} from "lucide-react";
import { FEATURE_PAGES } from "@/lib/featurePages";
import {
  ACCOUNT_LINKS,
  Avatar,
  NavAccount,
  useSiteVisitor,
  type SiteVisitor,
} from "./NavAccount";
import { SafeBoundary } from "@/components/SafeBoundary";
import { lockScroll } from "@/lib/scrollLock";

/* --------------------------------------------------------------------------
 * Menu content
 * ------------------------------------------------------------------------ */

type Tone = "violet" | "pink" | "sky" | "amber" | "emerald" | "indigo";
type Item = {
  href: string;
  label: string;
  body: string;
  icon: LucideIcon;
  tone: Tone;
  badge?: string;
};
type Group = {
  key: string;
  label: string;
  items: Item[];
  feature: { title: string; body: string; href: string; cta: string };
  wide?: boolean;
  /** Smaller links under the main items: the rest of the feature pages. */
  more?: { href: string; label: string }[];
};

// The Product menu comes from the feature-page list (lib/featurePages.ts), so the
// nav, the Features page, the home page and the sitemap always agree.
const PRODUCT_ITEMS: Item[] = FEATURE_PAGES.filter(
  (p) => p.tier === "primary",
).map((p) => ({
  href: p.path,
  label: p.name,
  body: p.short,
  icon: p.icon,
  tone: p.tone,
  badge: p.badge,
}));
const PRODUCT_MORE = [
  ...FEATURE_PAGES.filter(
    (p) => p.tier === "secondary" && p.path !== "/free-seo-checker",
  ).map((p) => ({ href: p.path, label: p.name })),
  { href: "/features", label: "All features" },
];

// Each item gets its own colour, so a menu reads at a glance.
const TONES: Record<Tone, string> = {
  violet:
    "from-[#B49CFF] to-[#6a52ff] shadow-[0_10px_22px_-10px_rgba(106,82,255,0.9)]",
  pink: "from-[#FFB3DC] to-[#E8559F] shadow-[0_10px_22px_-10px_rgba(232,85,159,0.85)]",
  sky: "from-[#9EE3FF] to-[#2A9BE0] shadow-[0_10px_22px_-10px_rgba(42,155,224,0.85)]",
  amber:
    "from-[#FFD98A] to-[#F29A1F] shadow-[0_10px_22px_-10px_rgba(242,154,31,0.85)]",
  emerald:
    "from-[#9BF0C8] to-[#18A874] shadow-[0_10px_22px_-10px_rgba(24,168,116,0.85)]",
  indigo:
    "from-[#A9B4FF] to-[#4453E0] shadow-[0_10px_22px_-10px_rgba(68,83,224,0.85)]",
};

const GROUPS: Group[] = [
  {
    key: "product",
    label: "Product",
    wide: true,
    items: PRODUCT_ITEMS,
    more: PRODUCT_MORE,
    feature: {
      title: "Free SEO checker",
      body: "Score any page in seconds. No sign-up, no card.",
      href: "/free-seo-checker",
      cta: "Check a page",
    },
  },
  {
    key: "solutions",
    label: "Solutions",
    wide: true,
    items: [
      {
        href: "/solutions/agencies",
        label: "For agencies",
        body: "Client workspaces and white-label reports.",
        icon: Building2,
        tone: "violet",
      },
      {
        href: "/solutions/content-teams",
        label: "For content teams",
        body: "Publish weekly without generic AI drafts.",
        icon: Users,
        tone: "pink",
      },
      {
        href: "/solutions/creators",
        label: "For creators",
        body: "Grow from every video you make.",
        icon: Clapperboard,
        tone: "amber",
      },
      {
        href: "/enterprise",
        label: "For enterprise",
        body: "Custom limits, seats and support.",
        icon: Shield,
        tone: "indigo",
      },
      {
        href: "/use-cases",
        label: "Use cases",
        body: "Six jobs people use Rankbist for.",
        icon: Workflow,
        tone: "amber",
      },
      {
        href: "/why-rankbist",
        label: "Why Rankbist",
        body: "What it checks that other tools don't.",
        icon: Telescope,
        tone: "emerald",
      },
      {
        href: "/how-it-works",
        label: "How it works",
        body: "From a check to a fix, step by step.",
        icon: Gauge,
        tone: "sky",
      },
    ],
    feature: {
      title: "From {fromPrice} a month", // the cheapest monthly price on sale, read from the live plans
      body: "About half what the big SEO suites charge.",
      href: "/pricing",
      cta: "See pricing",
    },
  },
  {
    key: "resources",
    label: "Resources",
    wide: true,
    items: [
      {
        href: "/blog",
        label: "Blog",
        body: "Practical SEO and writing notes.",
        icon: BookOpen,
        tone: "violet",
      },
      {
        href: "/faq",
        label: "FAQ",
        body: "Answers to common questions.",
        icon: HelpCircle,
        tone: "sky",
      },
      {
        href: "/our-data",
        label: "Our data",
        body: "Where every number comes from.",
        icon: Database,
        tone: "indigo",
      },
      {
        href: "/changelog",
        label: "Changelog",
        body: "What's new in Rankbist.",
        icon: History,
        tone: "amber",
      },
      {
        href: "/about",
        label: "About",
        body: "Why Rankbist exists.",
        icon: FileSearch,
        tone: "pink",
      },
      {
        href: "/contact",
        label: "Contact",
        body: "Talk to a person.",
        icon: Mail,
        tone: "emerald",
      },
      {
        href: "/affiliate",
        label: "Affiliate program",
        body: "Earn a recurring commission, for life.",
        icon: Handshake,
        tone: "violet",
      },
      {
        href: "/refer",
        label: "Refer a friend",
        body: "Invite friends, earn free credits.",
        icon: Gift,
        tone: "pink",
      },
    ],
    feature: {
      title: "New on the blog",
      body: "Notes on SEO in the age of AI answers.",
      href: "/blog",
      cta: "Read the blog",
    },
  },
];

const PLAIN = [{ href: "/pricing", label: "Pricing" }];

function isCurrent(pathname: string, href: string) {
  return href === "/"
    ? pathname === "/"
    : pathname === href || pathname.startsWith(`${href}/`);
}

// The cheapest monthly price on sale, from /api/billing/plans (admin → Pricing & plans), fetched once.
let fromPricePromise: Promise<string | null> | null = null;
function loadFromPrice(): Promise<string | null> {
  fromPricePromise ??= api
    .getPlans()
    .then((plans) => {
      const monthly = plans.flatMap((p) => planPrices(p)).filter((x) => x.billing_interval === "MONTHLY" && x.price_cents > 0 && (!x.currency || x.currency === "USD"));
      return monthly.length ? fallbackPrice(Math.min(...monthly.map((x) => x.price_cents)), "USD") : null;
    })
    .catch(() => null);
  return fromPricePromise;
}

/** A menu card title; "{fromPrice}" becomes the live starting price ("Free to start" until it's known). */
function MenuTitle({ text }: { text: string }) {
  const [price, setPrice] = useState<string | null>(null);
  useEffect(() => {
    if (text.includes("{fromPrice}")) loadFromPrice().then(setPrice);
  }, [text]);
  if (!text.includes("{fromPrice}")) return <>{text}</>;
  return <>{price ? text.replace("{fromPrice}", price) : "Free to start"}</>;
}

function NavBadge({ text }: { text: string }) {
  return (
    <span
      className={`rounded-full px-1.5 py-[1px] text-[0.58rem] font-extrabold uppercase tracking-[0.06em] ${
        text === "Free"
          ? "bg-good-pale text-good"
          : "bg-gradient-to-br from-violet to-violet-deep text-white"
      }`}
    >
      {text}
    </span>
  );
}

function IconTile({
  icon: Icon,
  tone,
  size = "md",
}: {
  icon: LucideIcon;
  tone: Tone;
  size?: "md" | "sm";
}) {
  return (
    <span
      className={`flex flex-shrink-0 items-center justify-center bg-gradient-to-br text-white ${TONES[tone]} ${
        size === "sm" ? "h-9 w-9 rounded-xl" : "h-10 w-10 rounded-[14px]"
      }`}
    >
      <Icon size={size === "sm" ? 17 : 19} strokeWidth={2.2} />
    </span>
  );
}

/* --------------------------------------------------------------------------
 * Desktop dropdown
 * ------------------------------------------------------------------------ */

function DesktopMenu({
  group,
  open,
  onOpen,
  onClose,
  pathname,
}: {
  group: Group;
  open: boolean;
  onOpen: () => void;
  onClose: () => void;
  pathname: string;
}) {
  const active =
    (group.items ?? []).some((i) => isCurrent(pathname, i.href)) ||
    (group.more ?? []).some((m) => isCurrent(pathname, m.href));
  return (
    <div
      className="relative"
      onMouseEnter={onOpen}
      onMouseLeave={onClose}
      data-nav-item
      data-nav-open={open || undefined}
      data-nav-active={active || undefined}
    >
      <button
        type="button"
        onClick={() => (open ? onClose() : onOpen())}
        aria-expanded={open}
        aria-haspopup="true"
        className={`relative z-10 flex h-9 items-center gap-1 rounded-full px-3.5 text-[0.875rem] font-semibold transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet/40 xl:px-4 ${
          open || active
            ? "text-violet-deep group-data-[dark=true]/nav:text-white"
            : "text-ink-soft hover:text-ink group-data-[dark=true]/nav:text-white/70 group-data-[dark=true]/nav:hover:text-white"
        }`}
      >
        {group.label}
        <ChevronDown
          size={14}
          strokeWidth={2.6}
          className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        // The top padding bridges the gap, so the pointer can travel into the panel.
        <div
          className={`absolute left-1/2 top-full z-30 -translate-x-1/2 pt-3 ${group.wide ? "w-[720px]" : "w-[520px]"}`}
        >
          <div className="nav-panel overflow-hidden rounded-[26px]">
            <div
              className="h-1 bg-gradient-to-r from-violet-deep via-violet to-pink"
              aria-hidden
            />
            <div
              className={`grid gap-2 p-3 ${group.wide ? "grid-cols-[1fr_1fr_220px]" : "grid-cols-[1fr_200px]"}`}
            >
              <ul
                className={`grid gap-1 ${group.wide ? "col-span-2 grid-cols-2" : "grid-cols-1"}`}
              >
                {(group.items ?? []).map((item) => (
                  <li key={item.href}>
                    <Link
                      href={item.href}
                      onClick={onClose}
                      aria-current={
                        isCurrent(pathname, item.href) ? "page" : undefined
                      }
                      className="group flex items-start gap-3 rounded-2xl p-3 transition-colors hover:bg-violet-pale/55 aria-[current=page]:bg-violet-pale/70"
                    >
                      <IconTile icon={item.icon} tone={item.tone} />
                      <span className="min-w-0">
                        {item.badge && (
                          <span className="mb-0.5 block leading-none">
                            <NavBadge text={item.badge} />
                          </span>
                        )}
                        <span className="flex items-center gap-1 text-[0.9rem] font-bold text-ink group-hover:text-violet-deep">
                          <span className="whitespace-nowrap">
                            {item.label}
                          </span>
                          <ArrowRight
                            size={13}
                            className="-translate-x-1 opacity-0 transition-all group-hover:translate-x-0 group-hover:opacity-100"
                          />
                        </span>
                        <span className="mt-0.5 block text-[0.78rem] leading-snug text-ink-soft">
                          {item.body}
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
                {group.more && (
                  <li
                    className={`mt-1 border-t border-violet/10 px-3 pb-1 pt-3 ${group.wide ? "col-span-2" : ""}`}
                  >
                    <span className="text-[0.68rem] font-extrabold uppercase tracking-[0.12em] text-ink-softer">
                      More features
                    </span>
                    <span className="mt-1.5 flex flex-wrap gap-x-1 gap-y-1">
                      {(group.more ?? []).map((m) => (
                        <Link
                          key={m.href}
                          href={m.href}
                          onClick={onClose}
                          aria-current={
                            isCurrent(pathname, m.href) ? "page" : undefined
                          }
                          className={`rounded-full px-2.5 py-1 text-[0.78rem] font-semibold transition-colors hover:bg-violet-pale hover:text-violet-deep aria-[current=page]:bg-violet-pale aria-[current=page]:text-violet-deep ${m.href === "/features" ? "text-violet-deep" : "text-ink-soft"}`}
                        >
                          {m.label}
                          {m.href === "/features" ? " →" : ""}
                        </Link>
                      ))}
                    </span>
                  </li>
                )}
              </ul>
              <div className="flex flex-col gap-2">
                {/* Top: the way in for new visitors (signed-in visitors get their dashboard). */}
                <Link
                  href="/signup"
                  onClick={onClose}
                  className="rb-guest group relative flex flex-col overflow-hidden rounded-[20px] border border-violet/20 bg-gradient-to-br from-white to-violet-pale/80 p-4"
                >
                  <span className="flex items-center gap-2">
                    <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-gradient-to-br from-[#B49CFF] to-violet-deep text-white shadow-[0_8px_18px_-8px_rgba(106,82,255,0.9)]">
                      <Sparkles size={15} />
                    </span>
                    <span className="text-[0.68rem] font-extrabold uppercase tracking-[0.12em] text-violet-deep">
                      New here?
                    </span>
                  </span>
                  <span className="mt-2.5 text-[0.98rem] font-extrabold leading-tight text-ink">
                    Start free in 30 seconds
                  </span>
                  <span className="mt-2 flex flex-col gap-1 text-[0.76rem] text-ink-soft">
                    <span className="flex items-center gap-1.5">
                      <Check
                        size={12}
                        strokeWidth={3}
                        className="text-violet-deep"
                      />{" "}
                      3 free checks every day
                    </span>
                    <span className="flex items-center gap-1.5">
                      <Check
                        size={12}
                        strokeWidth={3}
                        className="text-violet-deep"
                      />{" "}
                      No credit card needed
                    </span>
                  </span>
                  <span className="mt-3 inline-flex w-fit items-center gap-1.5 rounded-full bg-gradient-to-r from-violet to-violet-deep px-3.5 py-1.5 text-[0.78rem] font-extrabold text-white transition-transform group-hover:translate-x-0.5">
                    Create free account <ArrowRight size={13} />
                  </span>
                </Link>
                <Link
                  href="/dashboard"
                  onClick={onClose}
                  className="rb-member group hidden flex-col overflow-hidden rounded-[20px] border border-violet/20 bg-gradient-to-br from-white to-violet-pale/80 p-4"
                >
                  <span className="text-[0.68rem] font-extrabold uppercase tracking-[0.12em] text-violet-deep">
                    Welcome back
                  </span>
                  <span className="mt-1.5 text-[0.98rem] font-extrabold leading-tight text-ink">
                    Open your dashboard
                  </span>
                  <span className="mt-1 text-[0.76rem] leading-snug text-ink-soft">
                    Your reports, audits and tools.
                  </span>
                  <span className="mt-3 inline-flex w-fit items-center gap-1.5 rounded-full bg-gradient-to-r from-violet to-violet-deep px-3.5 py-1.5 text-[0.78rem] font-extrabold text-white">
                    Go to dashboard <ArrowRight size={13} />
                  </span>
                </Link>
                {/* Bottom: the featured free tool, as before. */}
                <Link
                  href={group.feature.href}
                  onClick={onClose}
                  className="nav-feature group relative flex flex-1 flex-col justify-end overflow-hidden rounded-[20px] p-5 text-white"
                >
                  <span className="relative text-[0.7rem] font-extrabold uppercase tracking-[0.12em] text-white/70">
                    Featured
                  </span>
                  <span className="relative mt-1.5 text-[1.12rem] font-extrabold leading-tight">
                    <MenuTitle text={group.feature.title} />
                  </span>
                  <span className="relative mt-1.5 text-[0.8rem] leading-snug text-white/75">
                    {group.feature.body}
                  </span>
                  <span className="relative mt-4 inline-flex w-fit items-center gap-1.5 rounded-full bg-white px-3.5 py-1.5 text-[0.78rem] font-extrabold text-violet-deep transition-transform group-hover:translate-x-0.5">
                    {group.feature.cta} <ArrowRight size={13} />
                  </span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* --------------------------------------------------------------------------
 * Sign-in area (no flash of "Log in" for signed-in visitors)
 * ------------------------------------------------------------------------ */

/** Before the sign-in is known (the server render, and the instant after a
 *  reload) both the guest buttons and an avatar placeholder are rendered;
 *  the `data-auth` flag the layout sets before the first paint decides with
 *  CSS which one is visible. See .rb-guest / .rb-member in globals.css. */
function AuthArea({
  visitor,
  onSignOut,
}: {
  visitor: SiteVisitor | "pending" | null;
  onSignOut: () => void;
}) {
  // Phones reach the account from the profile tab at the bottom, so the top
  // bar only keeps a quick way in: "Sign up" for guests, "Dashboard" for members.
  const phoneDashboard = (
    <Link
      href="/dashboard"
      className="inline-flex h-9 items-center gap-1 rounded-full bg-gradient-to-br from-[#A488FF] via-violet to-violet-deep text-white shadow-[0_12px_26px_-12px_rgba(143,103,255,0.85),inset_0_1px_0_rgba(255,255,255,0.3)] px-3.5 text-[0.8rem] font-bold md:hidden"
    >
      Dashboard <ArrowRight size={13} />
    </Link>
  );
  if (visitor !== null)
    return (
      <>
        {phoneDashboard}
        <div className="hidden md:block">
          <NavAccount visitor={visitor} onSignOut={onSignOut} />
        </div>
      </>
    );
  return (
    <>
      <span className="hidden md:contents">
        <span
          className="rb-member hidden items-center gap-1 rounded-full border border-violet-light bg-white py-[3px] pl-[3px] pr-2.5"
          aria-hidden
        >
          <span className="h-7 w-7 animate-pulse rounded-full bg-gradient-to-br from-[#D9CCFF] to-violet-pale lg:h-8 lg:w-8" />
          <span className="h-2.5 w-12 animate-pulse rounded-full bg-violet-pale" />
        </span>
      </span>
      <span className="md:hidden">
        <span className="rb-member hidden">{phoneDashboard}</span>
      </span>
      <span className="rb-guest flex items-center gap-1.5">
        <Link
          href="/login"
          className="hidden h-10 items-center rounded-full px-4 text-[0.875rem] font-semibold text-ink-soft transition-colors duration-500 hover:text-ink lg:inline-flex group-data-[dark=true]/nav:text-white/70 group-data-[dark=true]/nav:hover:text-white"
        >
          Log in
        </Link>
        <Link
          href="/signup"
          className="nav-cta group relative inline-flex h-9 items-center gap-1.5 overflow-hidden rounded-full bg-gradient-to-br from-[#A488FF] via-violet to-violet-deep text-white shadow-[0_12px_26px_-12px_rgba(143,103,255,0.85),inset_0_1px_0_rgba(255,255,255,0.3)] px-4 text-[0.8rem] font-bold transition-transform duration-200 hover:-translate-y-0.5 sm:h-10 sm:px-5 sm:text-[0.86rem]"
        >
          <span className="sm:hidden">Sign up</span>
          <span className="hidden sm:inline">Start free</span>
          <ArrowRight
            size={14}
            className="hidden transition-transform group-hover:translate-x-0.5 sm:block"
          />
        </Link>
      </span>
    </>
  );
}

/* --------------------------------------------------------------------------
 * Phone tab bar (below 768 px) and the account sheet it opens
 * ------------------------------------------------------------------------ */

function ProfileSheet({
  onClose,
  visitor,
  onSignOut,
}: {
  onClose: () => void;
  visitor: SiteVisitor | "pending" | null;
  onSignOut: () => void;
}) {
  const known = visitor && visitor !== "pending" ? visitor : null;
  const packageName = known?.package_name ?? known?.plan_name ?? null;
  return (
    <div
      className="fixed inset-0 z-[120] md:hidden"
      role="dialog"
      aria-modal="true"
      aria-label="Your account"
    >
      <div
        className="nav-backdrop absolute inset-0 bg-[#140f2e]/45 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="rb-sheet-up absolute inset-x-0 bottom-0 rounded-t-[28px] bg-gradient-to-b from-[#F7F4FF] to-white px-4 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-2.5 shadow-[0_-30px_80px_-30px_rgba(60,30,160,0.45)]">
        <div
          className="mx-auto mb-3 h-1.5 w-11 rounded-full bg-violet/20"
          aria-hidden
        />
        <div className="mb-3 flex items-center justify-between">
          <span className="text-[1.05rem] font-extrabold text-ink">
            {visitor ? "Your account" : "Welcome to Rankbist"}
          </span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-9 w-9 items-center justify-center rounded-full bg-white text-ink ring-1 ring-violet/15"
          >
            <X size={18} />
          </button>
        </div>

        {visitor ? (
          <div className="space-y-2.5">
            <div className="flex items-center gap-3 rounded-[20px] bg-gradient-to-br from-violet-pale to-white p-3 ring-1 ring-violet/15">
              <Avatar visitor={visitor} size="md" />
              <div className="min-w-0 flex-1">
                <div className="truncate text-[0.95rem] font-extrabold">
                  {known ? known.name || "Your account" : "Signing you in…"}
                </div>
                {known && (
                  <div className="truncate text-[0.78rem] text-ink-soft">
                    {known.email}
                  </div>
                )}
              </div>
              {packageName && (
                <span className="rounded-full bg-white px-2.5 py-0.5 text-[0.7rem] font-extrabold text-violet-deep ring-1 ring-violet/20">
                  {packageName}
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 gap-2">
              {ACCOUNT_LINKS.map((l) => (
                <Link
                  key={l.href}
                  href={l.href}
                  onClick={onClose}
                  className="flex items-center gap-2 rounded-2xl bg-white px-3 py-3 text-[0.85rem] font-bold text-ink ring-1 ring-violet/10 active:bg-violet-pale"
                >
                  <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-violet-pale text-violet-deep">
                    <l.icon size={15} />
                  </span>
                  {l.label}
                </Link>
              ))}
            </div>
            <button
              type="button"
              onClick={() => {
                onClose();
                onSignOut();
              }}
              className="flex w-full items-center justify-center gap-2 rounded-2xl py-3 text-[0.88rem] font-bold text-warn active:bg-warn-pale"
            >
              <LogOut size={16} /> Log out
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-3 rounded-[20px] bg-gradient-to-br from-violet-pale to-white p-3.5 ring-1 ring-violet/15">
              <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#A488FF] via-violet to-violet-deep text-white">
                <UserRound size={20} />
              </span>
              <div className="min-w-0 text-[0.8rem] leading-snug text-ink-soft">
                <div className="text-[0.95rem] font-extrabold text-ink">
                  Start free in 30 seconds
                </div>
                3 free checks a day · a Creator Studio video to try · no card
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2.5">
              <Link
                href="/login"
                onClick={onClose}
                className="flex h-12 items-center justify-center rounded-full bg-white text-[0.95rem] font-extrabold text-ink ring-[1.5px] ring-violet-light"
              >
                Log in
              </Link>
              <Link
                href="/signup"
                onClick={onClose}
                className="flex h-12 items-center justify-center gap-1.5 rounded-full bg-gradient-to-br from-[#A488FF] via-violet to-violet-deep text-[0.95rem] font-extrabold text-white shadow-[0_14px_30px_-12px_rgba(143,103,255,0.9)]"
              >
                Start free <ArrowRight size={15} />
              </Link>
            </div>
            <Link
              href="/free-seo-checker"
              onClick={onClose}
              className="flex items-center justify-center gap-1.5 py-1 text-[0.85rem] font-bold text-violet-deep"
            >
              Or check a page without an account <ArrowRight size={14} />
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

function TabBar({
  pathname,
  visitor,
  menuOpen,
  profileOpen,
  onMenu,
  onProfile,
}: {
  pathname: string;
  visitor: SiteVisitor | "pending" | null;
  menuOpen: boolean;
  profileOpen: boolean;
  onMenu: () => void;
  onProfile: () => void;
}) {
  const sheetOpen = menuOpen || profileOpen;
  const homeActive = !sheetOpen && pathname === "/";
  const pricingActive = !sheetOpen && isCurrent(pathname, "/pricing");
  const supportActive = !sheetOpen && isCurrent(pathname, "/contact");

  // Five cells in a fully rounded bar. Home is in the middle and raised. Every icon is the same size and weight; the
  // selected cell gets a violet circle (white icon and label), so spacing is identical
  // on the left and right and nothing changes size when you switch tabs.
  const tab = (active: boolean) =>
    `relative z-10 flex h-[48px] w-[48px] flex-col items-center justify-center gap-[3px] rounded-full outline-none transition-colors duration-300 [-webkit-tap-highlight-color:transparent] active:scale-95 ${
      active ? "text-white" : "text-[#7d7a94]"
    }`;
  const bar = (active: boolean) => (
    <span
      aria-hidden
      className={`absolute left-1/2 top-1/2 -z-10 h-[48px] w-[48px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-gradient-to-b from-[#8f78ff] to-[#4b33e0] shadow-[0_10px_22px_-8px_rgba(75,51,224,0.75),inset_0_1px_0_rgba(255,255,255,0.3)] transition-all duration-300 ease-[cubic-bezier(.2,.8,.2,1)] ${active ? "scale-100 opacity-100" : "scale-75 opacity-0"}`}
    />
  );
  const label = "text-[9px] font-bold leading-none tracking-[0.01em]";
  const icon = (active: boolean) => ({
    size: 17,
    strokeWidth: active ? 2.2 : 1.9,
  });

  return (
    <nav
      className="rb-tabbar fixed inset-x-5 z-[110] rounded-full border border-white/80 bg-white/80 p-1 shadow-[0_14px_30px_-14px_rgba(40,25,110,0.5),0_2px_6px_-2px_rgba(40,25,110,0.14)] ring-1 ring-[#6a52ff]/10 backdrop-blur-md md:hidden"
      style={{ bottom: "max(env(safe-area-inset-bottom), 8px)" }}
      aria-label="Quick navigation"
    >
      <div className="flex items-center justify-between">
        <Link
          href="/pricing"
          aria-current={pricingActive ? "page" : undefined}
          className={tab(pricingActive)}
        >
          {bar(pricingActive)}
          <Tag {...icon(pricingActive)} />
          <span className={label}>Pricing</span>
        </Link>
        <Link
          href="/contact"
          aria-current={supportActive ? "page" : undefined}
          className={tab(supportActive)}
        >
          {bar(supportActive)}
          <LifeBuoy {...icon(supportActive)} />
          <span className={label}>Support</span>
        </Link>
        {/* Home sits in the middle and is raised above the bar: a floating button that fills violet when you are on the home page. */}
        <Link
          href="/"
          aria-current={homeActive ? "page" : undefined}
          aria-label="Home"
          className="relative z-10 flex h-[48px] w-[48px] flex-col items-center justify-end pb-[6px] outline-none [-webkit-tap-highlight-color:transparent]"
        >
          <span
            aria-hidden
            className={`absolute -top-[18px] left-1/2 flex h-[54px] w-[54px] -translate-x-1/2 items-center justify-center rounded-full ring-[4px] ring-white transition-all duration-300 ease-[cubic-bezier(.2,.8,.2,1)] ${
              homeActive
                ? "bg-gradient-to-b from-[#8f78ff] to-[#4b33e0] text-white shadow-[0_14px_26px_-8px_rgba(75,51,224,0.85),inset_0_1px_0_rgba(255,255,255,0.3)]"
                : "bg-violet-pale text-violet-deep shadow-[0_8px_18px_-10px_rgba(75,51,224,0.55)]"
            }`}
          >
            <Home size={22} strokeWidth={2.2} />
          </span>
        </Link>
        <button
          type="button"
          onClick={onProfile}
          aria-expanded={profileOpen}
          aria-label={visitor ? "Your account" : "Log in or sign up"}
          className={tab(profileOpen)}
        >
          {bar(profileOpen)}
          {visitor ? (
            <span
              className={`-my-[3.5px] flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full ${profileOpen ? "ring-2 ring-violet" : "ring-1 ring-black/10"}`}
            >
              <Avatar visitor={visitor} size="xs" />
            </span>
          ) : (
            <span className="flex h-[17px] w-[17px] items-center justify-center">
              <UserRound {...icon(profileOpen)} className="rb-guest" />
              {/* Before the sign-in is confirmed, a stored one already shows an avatar. */}
              <span className="rb-member hidden -my-[3.5px] h-6 w-6 flex-shrink-0">
                <Avatar visitor="pending" size="xs" />
              </span>
            </span>
          )}
          <span className={label}>{visitor ? "Account" : "Profile"}</span>
        </button>
        <button
          type="button"
          onClick={onMenu}
          aria-expanded={menuOpen}
          aria-label="Menu"
          className={tab(menuOpen)}
        >
          {bar(menuOpen)}
          <Menu {...icon(menuOpen)} />
          <span className={label}>Menu</span>
        </button>
      </div>
    </nav>
  );
}

/* --------------------------------------------------------------------------
 * Mobile menu
 * ------------------------------------------------------------------------ */

function MobileMenu({
  onClose,
  visitor,
  onSignOut,
  pathname,
}: {
  onClose: () => void;
  visitor: SiteVisitor | "pending" | null;
  onSignOut: () => void;
  pathname: string;
}) {
  // One group is always showing: the one the visitor is on, else Product.
  const [section, setSection] = useState<string>(
    () =>
      GROUPS.find((g) => g.items.some((i) => isCurrent(pathname, i.href)))
        ?.key ?? "product",
  );
  const group = GROUPS.find((g) => g.key === section) ?? GROUPS[0];
  const known = visitor && visitor !== "pending" ? visitor : null;
  const packageName = known?.package_name ?? known?.plan_name ?? null;

  return (
    <div
      className="fixed inset-0 z-[120] lg:hidden"
      role="dialog"
      aria-modal="true"
      aria-label="Menu"
    >
      <div
        className="nav-backdrop absolute inset-0 bg-[#140f2e]/45 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="nav-sheet absolute inset-y-0 right-0 flex w-full max-w-[430px] flex-col bg-gradient-to-b from-[#F7F4FF] via-white to-[#FBF8FF] shadow-[-30px_0_80px_-30px_rgba(60,30,160,0.45)]">
        <div className="flex items-center justify-between px-4 pb-3 pt-4">
          <Link
            href="/"
            onClick={onClose}
            className="flex items-center pl-1"
            aria-label="Rankbist home"
          >
            <Image
              src="/rankbist.png"
              alt="Rankbist"
              width={132}
              height={25}
              className="h-6 w-auto"
            />
          </Link>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close menu"
            className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-ink shadow-[0_6px_18px_-10px_rgba(106,82,255,0.6)] ring-1 ring-violet/15"
          >
            <X size={20} />
          </button>
        </div>

        {/* Section switcher: always visible, so it is clear where you are and what else there is. */}
        <div className="px-4 pb-3">
          <div
            role="tablist"
            aria-label="Menu sections"
            className="grid grid-cols-3 gap-1 rounded-full bg-violet-pale/80 p-1 ring-1 ring-violet/10"
          >
            {GROUPS.map((g) => {
              const on = g.key === section;
              return (
                <button
                  key={g.key}
                  type="button"
                  role="tab"
                  id={`mm-tab-${g.key}`}
                  aria-selected={on}
                  aria-controls="mm-panel"
                  onClick={() => setSection(g.key)}
                  className={`h-10 rounded-full text-[0.86rem] font-extrabold transition-colors ${
                    on
                      ? "bg-white text-violet-deep shadow-[0_6px_16px_-8px_rgba(106,82,255,0.7)]"
                      : "text-ink-soft active:bg-white/60"
                  }`}
                >
                  {g.label}
                </button>
              );
            })}
          </div>
        </div>

        <nav
          id="mm-panel"
          role="tabpanel"
          aria-labelledby={`mm-tab-${group.key}`}
          className="flex-1 space-y-3 overflow-y-auto px-4 pb-4"
          aria-label="Main"
        >
          {/* The one thing to try first for this section */}
          <Link
            href={group.feature.href}
            onClick={onClose}
            className="block rounded-[22px] bg-gradient-to-br from-[#A488FF] via-violet to-violet-deep p-4 text-white shadow-[0_16px_32px_-18px_rgba(106,82,255,0.9)] active:scale-[0.99]"
          >
            <span className="block text-[1.02rem] font-extrabold">
              <MenuTitle text={group.feature.title} />
            </span>
            <span className="mt-0.5 block text-[0.82rem] leading-snug text-white/80">
              {group.feature.body}
            </span>
            <span className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-white px-3.5 py-1.5 text-[0.82rem] font-extrabold text-violet-deep">
              {group.feature.cta} <ArrowRight size={14} />
            </span>
          </Link>

          {/* Every row is a link: icon, name, one line about it, and an arrow. */}
          <ul className="divide-y divide-violet/10 overflow-hidden rounded-[22px] bg-white ring-1 ring-violet/10">
            {group.items.map((item) => (
              <li key={item.href}>
                <Link
                  href={item.href}
                  onClick={onClose}
                  aria-current={
                    isCurrent(pathname, item.href) ? "page" : undefined
                  }
                  className="flex items-center gap-3 px-3.5 py-3 active:bg-violet-pale aria-[current=page]:bg-violet-pale/70"
                >
                  <IconTile icon={item.icon} tone={item.tone} />
                  <span className="min-w-0 flex-1">
                    <span className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[0.95rem] font-extrabold leading-tight text-ink">
                      {item.label}
                      {item.badge && <NavBadge text={item.badge} />}
                    </span>
                    <span className="mt-0.5 line-clamp-2 block text-[0.78rem] leading-snug text-ink-soft">
                      {item.body}
                    </span>
                  </span>
                  <ChevronRight
                    size={18}
                    strokeWidth={2.4}
                    className="flex-shrink-0 text-ink-softer"
                    aria-hidden
                  />
                </Link>
              </li>
            ))}
          </ul>

          {group.more && (
            <div>
              <h3 className="px-1 pb-2 text-[0.68rem] font-extrabold uppercase tracking-[0.12em] text-ink-softer">
                More features
              </h3>
              <div className="flex flex-wrap gap-1.5">
                {group.more.map((m) => (
                  <Link
                    key={m.href}
                    href={m.href}
                    onClick={onClose}
                    aria-current={
                      isCurrent(pathname, m.href) ? "page" : undefined
                    }
                    className={`rounded-full px-3 py-2 text-[0.82rem] font-bold ring-1 active:bg-violet-pale aria-[current=page]:bg-violet-pale ${
                      m.href === "/features"
                        ? "bg-violet-pale text-violet-deep ring-violet/25"
                        : "bg-white text-ink ring-violet/15"
                    }`}
                  >
                    {m.label}
                    {m.href === "/features" ? " →" : ""}
                  </Link>
                ))}
              </div>
            </div>
          )}

          {PLAIN.map((l) => (
            <Link
              key={l.href}
              href={l.href}
              onClick={onClose}
              className="flex items-center justify-between gap-2 rounded-[22px] bg-white px-4 py-3.5 text-[0.98rem] font-extrabold text-ink ring-1 ring-violet/10 active:bg-violet-pale"
            >
              {l.label}
              <span className="flex items-center gap-2">
                <span className="rounded-full bg-gradient-to-r from-[#FFE7A8] to-[#FFC7E6] px-2.5 py-0.5 text-[0.72rem] font-extrabold text-[#8a3d00]">
                  from $22
                </span>
                <ChevronRight
                  size={18}
                  strokeWidth={2.4}
                  className="text-ink-softer"
                  aria-hidden
                />
              </span>
            </Link>
          ))}
        </nav>

        <div className="border-t border-violet/10 bg-white/80 px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-3.5 backdrop-blur">
          {visitor ? (
            <div className="space-y-2.5">
              <div className="flex items-center gap-3 rounded-[20px] bg-gradient-to-br from-violet-pale to-white p-3 ring-1 ring-violet/15">
                {known?.picture_url ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={known.picture_url}
                    alt=""
                    className="h-11 w-11 rounded-full object-cover"
                    referrerPolicy="no-referrer"
                  />
                ) : (
                  <span className="flex h-11 w-11 items-center justify-center rounded-full bg-gradient-to-br from-[#A488FF] via-violet to-violet-deep text-[0.85rem] font-extrabold text-white">
                    {known
                      ? (known.name || known.email).slice(0, 2).toUpperCase()
                      : ""}
                  </span>
                )}
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[0.95rem] font-extrabold">
                    {known ? known.name || "Your account" : "Signing you in…"}
                  </div>
                  {known && (
                    <div className="truncate text-[0.78rem] text-ink-soft">
                      {known.email}
                    </div>
                  )}
                </div>
                {packageName && (
                  <span className="rounded-full bg-white px-2.5 py-0.5 text-[0.7rem] font-extrabold text-violet-deep ring-1 ring-violet/20">
                    {packageName}
                  </span>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2">
                {ACCOUNT_LINKS.map((l) => (
                  <Link
                    key={l.href}
                    href={l.href}
                    onClick={onClose}
                    className="flex items-center gap-2 rounded-2xl bg-white px-3 py-2.5 text-[0.85rem] font-bold text-ink ring-1 ring-violet/10 active:bg-violet-pale"
                  >
                    <l.icon size={16} className="text-violet-deep" />
                    {l.label}
                  </Link>
                ))}
              </div>
              <button
                type="button"
                onClick={() => {
                  onClose();
                  onSignOut();
                }}
                className="flex w-full items-center justify-center gap-2 rounded-2xl py-2.5 text-[0.88rem] font-bold text-warn active:bg-warn-pale"
              >
                <LogOut size={16} /> Log out
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2.5">
              <Link
                href="/login"
                onClick={onClose}
                className="flex h-12 items-center justify-center rounded-full bg-white text-[0.95rem] font-extrabold text-ink ring-[1.5px] ring-violet-light"
              >
                Log in
              </Link>
              <Link
                href="/signup"
                onClick={onClose}
                className="flex h-12 items-center justify-center gap-1.5 rounded-full bg-gradient-to-br from-[#A488FF] via-violet to-violet-deep text-[0.95rem] font-extrabold text-white shadow-[0_14px_30px_-12px_rgba(143,103,255,0.9)]"
              >
                Start free <ArrowRight size={15} />
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* --------------------------------------------------------------------------
 * The bar
 * ------------------------------------------------------------------------ */

function NavUnsafe() {
  const pathname = usePathname() ?? "/";
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [menu, setMenu] = useState<string | null>(null);
  const [dark, setDark] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLElement>(null);
  const [pill, setPill] = useState<{ x: number; w: number; on: boolean }>({
    x: 0,
    w: 0,
    on: false,
  });
  // Signed-in visitors get a profile menu instead of the sign-in buttons.
  const { visitor, signOut } = useSiteVisitor();

  const openMenu = useCallback((key: string) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setMenu(key);
  }, []);
  const closeMenuSoon = useCallback(() => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setMenu(null), 140);
  }, []);

  useEffect(() => {
    /* The bar has two states, top of the page and scrolled. Only its width and colours
       change between them, as one timed CSS transition, so it looks the same however fast
       you scroll. A small dead zone (8px / 48px) stops it flickering near the threshold. */
    let frame = 0;
    const apply = () => {
      frame = 0;
      const y = window.scrollY;
      setDark((was) => (was ? y > 8 : y > 48));
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(apply);
    };
    apply();
    window.addEventListener("scroll", onScroll, { passive: true });
    function onDown(e: MouseEvent) {
      if (barRef.current && !barRef.current.contains(e.target as Node))
        setMenu(null);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setMenu(null);
        setMobileOpen(false);
        setProfileOpen(false);
      }
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (frame) cancelAnimationFrame(frame);
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
      if (closeTimer.current) clearTimeout(closeTimer.current);
    };
  }, []);

  /* The white pill in the menu track glides to the item under the pointer
     (or with keyboard focus), rests on an open menu, and otherwise marks the
     section of the site you are in. */
  const placePill = useCallback((el: Element | null | undefined) => {
    const track = trackRef.current;
    if (!track || !el) return setPill((p) => ({ ...p, on: false }));
    const t = track.getBoundingClientRect();
    const r = el.getBoundingClientRect();
    setPill({ x: r.left - t.left, w: r.width, on: true });
  }, []);
  const restPill = useCallback(() => {
    const track = trackRef.current;
    placePill(
      track?.querySelector("[data-nav-open]") ??
        track?.querySelector("[data-nav-active]"),
    );
  }, [placePill]);
  useEffect(() => {
    restPill();
  }, [menu, pathname, restPill]);
  useEffect(() => {
    window.addEventListener("resize", restPill);
    return () => window.removeEventListener("resize", restPill);
  }, [restPill]);

  // The page underneath doesn't scroll while a phone menu is open.
  const sheetOpen = mobileOpen || profileOpen;
  useEffect(() => {
    if (!sheetOpen) return;
    return lockScroll();
  }, [sheetOpen]);

  return (
    <div className="sticky top-0 z-[100] px-3 pt-2 sm:px-6 sm:pt-3 before:pointer-events-none before:absolute before:inset-x-0 before:top-0 before:h-[calc(100%+10px)] before:content-[''] before:backdrop-blur-md before:[mask-image:linear-gradient(#000_45%,transparent)] sm:before:hidden">
      <style>{`
        .nav-cta::after { content: ""; position: absolute; inset: 0; background: linear-gradient(110deg, transparent 30%, rgba(255,255,255,.22) 50%, transparent 70%); transform: translateX(-120%); transition: transform .7s ease }
        .nav-cta:hover::after { transform: translateX(120%) }
        @media (prefers-reduced-motion: reduce) { .nav-cta::after { display: none } }
        /* The bar keeps its size, logo, links and buttons. Scrolling only narrows it (wide screens)
           and shifts its colours, both on the same 600ms ease so it feels like one soft movement. */
        .rb-navbar { max-width: 1200px; height: 58px; transition: max-width .6s cubic-bezier(.22,.8,.24,1), background-color .6s ease, border-color .6s ease, box-shadow .6s ease }
        @media (min-width: 640px) { .rb-navbar { height: 66px } }
        @media (min-width: 1024px) { .rb-navbar[data-dark="true"] { max-width: 880px } }
        .rb-navbar-dark { opacity: 0; transition: opacity .6s ease }
        .rb-navbar[data-dark="true"] .rb-navbar-dark { opacity: 1 }
        .rb-navbar-word { filter: brightness(1) invert(0); transition: filter .6s ease }
        .rb-navbar[data-dark="true"] .rb-navbar-word { filter: brightness(0) invert(1) }
        /* the phone tab bar now floats above the bottom edge: keep content and the cookie banner clear of it */
        @media (max-width: 767.98px) {
          body:has(.rb-tabbar) { padding-bottom: calc(68px + max(env(safe-area-inset-bottom), 8px)) }
          body:has(.rb-tabbar) [aria-labelledby="rb-cookie-title"] { bottom: calc(72px + max(env(safe-area-inset-bottom), 8px)) }
        }
      `}</style>
      <div
        ref={barRef}
        data-dark={dark}
        // Max 1200px, fully rounded. Scrolled: narrower on wide screens and dark; same size otherwise.
        className={`rb-navbar group/nav relative mx-auto flex items-center justify-between gap-3 rounded-full border pl-4 pr-2 backdrop-blur-md sm:pl-5 ${
          dark
            ? "border-white/10 bg-[#17122f]/40 shadow-[0_18px_44px_-18px_rgba(20,12,60,0.75),0_1px_3px_rgba(20,12,60,0.2)]"
            : "border-white/80 bg-white/70 shadow-[0_12px_32px_-24px_rgba(60,35,160,0.45)]"
        }`}
      >
        {/* The dark layer: fades in with the scroll */}
        <span
          aria-hidden
          className="rb-navbar-dark pointer-events-none absolute inset-0 rounded-full bg-[linear-gradient(120deg,rgba(20,15,51,0.94),rgba(34,26,92,0.94)_55%,rgba(27,21,64,0.94))] ring-1 ring-inset ring-white/10"
        />

        <Link
          href="/"
          aria-label="Rankbist home"
          className="relative flex h-9 w-[128px] flex-shrink-0 items-center overflow-hidden rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet/40 sm:w-[168px]"
        >
          {/* the full wordmark */}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/rankbist.png"
            alt="Rankbist"
            width={1781}
            height={375}
            fetchPriority="high"
            className="rb-navbar-word absolute left-0 top-1/2 h-[20px] w-auto max-w-none -translate-y-1/2 sm:h-[26px]"
          />
        </Link>

        <nav
          ref={trackRef}
          className="relative hidden items-center rounded-full bg-[#f3f0fc]/80 p-1 ring-1 ring-[#e7e2f8] transition-colors duration-300 lg:flex group-data-[dark=true]/nav:bg-white/[0.07] group-data-[dark=true]/nav:ring-white/10"
          aria-label="Main"
          onMouseOver={(e) =>
            placePill((e.target as Element).closest("[data-nav-item]"))
          }
          onFocus={(e) =>
            placePill((e.target as Element).closest("[data-nav-item]"))
          }
          onMouseLeave={restPill}
          onBlur={restPill}
        >
          {/* the gliding pill */}
          <span
            aria-hidden
            className="pointer-events-none absolute left-0 top-1 h-9 rounded-full bg-white shadow-[0_6px_16px_-8px_rgba(60,35,160,0.45),0_1px_2px_rgba(60,35,160,0.08)] ring-1 ring-[#ebe6fb] transition-[transform,width,opacity,background-color] duration-300 group-data-[dark=true]/nav:bg-white/[0.14] group-data-[dark=true]/nav:shadow-none group-data-[dark=true]/nav:ring-white/15 ease-[cubic-bezier(.2,.8,.2,1)] motion-reduce:transition-none"
            style={{
              transform: `translateX(${pill.x}px)`,
              width: pill.w,
              opacity: pill.on ? 1 : 0,
            }}
          />
          {GROUPS.map((g) => (
            <DesktopMenu
              key={g.key}
              group={g}
              open={menu === g.key}
              onOpen={() => openMenu(g.key)}
              onClose={closeMenuSoon}
              pathname={pathname}
            />
          ))}
          {PLAIN.map((l) => (
            <Link
              key={l.href}
              href={l.href}
              data-nav-item
              data-nav-active={isCurrent(pathname, l.href) || undefined}
              aria-current={isCurrent(pathname, l.href) ? "page" : undefined}
              className="relative z-10 flex h-9 items-center rounded-full px-3.5 text-[0.875rem] font-semibold text-ink-soft transition-colors duration-200 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet/40 aria-[current=page]:text-violet-deep xl:px-4 group-data-[dark=true]/nav:text-white/70 group-data-[dark=true]/nav:hover:text-white group-data-[dark=true]/nav:aria-[current=page]:text-white"
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="relative flex items-center gap-1.5">
          <AuthArea visitor={visitor} onSignOut={signOut} />
          <button
            type="button"
            aria-label="Open menu"
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen(true)}
            className="hidden h-10 w-10 items-center justify-center rounded-full bg-[#f3f0fc] text-ink ring-1 ring-[#e7e2f8] transition-colors hover:bg-violet-pale hover:text-violet-deep md:flex lg:hidden group-data-[dark=true]/nav:bg-white/10 group-data-[dark=true]/nav:text-white group-data-[dark=true]/nav:ring-white/15 group-data-[dark=true]/nav:hover:bg-white/20 group-data-[dark=true]/nav:hover:text-white"
          >
            <Menu size={19} strokeWidth={2.2} />
          </button>
        </div>
      </div>

      {mobileOpen && (
        <MobileMenu
          onClose={() => setMobileOpen(false)}
          visitor={visitor}
          onSignOut={signOut}
          pathname={pathname}
        />
      )}
      {profileOpen && (
        <ProfileSheet
          onClose={() => setProfileOpen(false)}
          visitor={visitor}
          onSignOut={signOut}
        />
      )}
      <TabBar
        pathname={pathname}
        visitor={visitor}
        menuOpen={mobileOpen}
        profileOpen={profileOpen}
        onMenu={() => {
          setProfileOpen(false);
          setMobileOpen((v) => !v);
        }}
        onProfile={() => {
          setMobileOpen(false);
          setProfileOpen((v) => !v);
        }}
      />
    </div>
  );
}

/** Nav, kept on its own: if it ever fails to render, only this part shows a note. */
export function Nav() {
  return (
    <>
      <SafeBoundary name="LaunchOfferBar" fallback="none">
        <LaunchOfferBar />
      </SafeBoundary>
      <SafeBoundary name="OfferPopup" fallback="none">
        <OfferPopup />
      </SafeBoundary>
      <SafeBoundary name="Nav" fallback={<NavFallback />}>
        <NavUnsafe />
      </SafeBoundary>
    </>
  );
}

/** What the bar shows if it ever fails: the logo and the three links people need most. */
function NavFallback() {
  return (
    <div className="sticky top-0 z-[100] px-3 pt-2 sm:px-6 sm:pt-3 before:pointer-events-none before:absolute before:inset-x-0 before:top-0 before:h-[calc(100%+10px)] before:content-[''] before:backdrop-blur-md before:[mask-image:linear-gradient(#000_45%,transparent)] sm:before:hidden">
      <div className="mx-auto flex h-[58px] max-w-[1200px] items-center justify-between rounded-full border border-white/80 bg-white/85 pl-5 pr-2 shadow-[0_12px_32px_-24px_rgba(60,35,160,0.45)] backdrop-blur-md">
        <Link href="/" aria-label="Rankbist home">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/rankbist.png"
            alt="Rankbist"
            width={1781}
            height={375}
            className="h-[22px] w-auto"
          />
        </Link>
        <nav className="flex items-center gap-1 text-[0.86rem] font-semibold text-ink-soft">
          <Link
            href="/pricing"
            className="rounded-full px-3 py-2 hover:text-ink"
          >
            Pricing
          </Link>
          <Link
            href="/login"
            className="hidden rounded-full px-3 py-2 hover:text-ink sm:inline"
          >
            Log in
          </Link>
          <Link
            href="/signup"
            className="rounded-full bg-[#17142b] px-4 py-2 font-bold text-white"
          >
            Start free
          </Link>
        </nav>
      </div>
    </div>
  );
}
